import { DrawerProps, Flex, Text } from '@chakra-ui/react';
import { mealPlanMolecule } from '@web-app/atoms/charting/mealPlanMolecule';
import { CreateMealPlan } from '@web-app/components/CreateMealPlan/index';
import { MealPlanWizardFooter } from '@web-app/components/CreateMealPlan/MealPlanWizardFooter';
import { MealPlanWizardHeader } from '@web-app/components/CreateMealPlan/MealPlanWizardHeader';
import {
  MealPlanAnalyticsContext,
  MealPlanView,
} from '@web-app/components/CreateMealPlan/utils';
import { SessionActivityDrawer } from '@web-app/components/SessionActivityDrawer/SessionActivityDrawer';
import { useUpdateMealPlanInstructions } from '@web-app/hooks/mutations/meal-plan/update-meal-plan-instructions';
import { useUpdateMealPlanSummary } from '@web-app/hooks/mutations/meal-plan/update-meal-plan-summary';
import { useMealPlanMutations } from '@web-app/hooks/useMealPlanMutations';
import { useMolecule } from 'bunshi/react';
import { useAtomValue } from 'jotai';
import { FC, useCallback, useState } from 'react';

export interface MealPlanInSessionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  dailyRoomName: string;
  patientName?: string;
  placement?: DrawerProps['placement'];
  usePreviousAppointment?: boolean;
  context?: MealPlanAnalyticsContext;
}

export const MealPlanInSessionDrawer: FC<MealPlanInSessionDrawerProps> = ({
  isOpen,
  onClose,
  dailyRoomName,
  patientName,
  placement,
  usePreviousAppointment = true,
  context = 'in_session',
}) => {
  const [isReadOnly, setIsReadOnly] = useState(false);

  const {
    activeMealPlanStepAtom,
    mealPlanInstructionsAtom,
    mealPlanSummaryAtom,
    detailedMealPlanEditModeAtom,
  } = useMolecule(mealPlanMolecule);
  const activeMealPlanStep = useAtomValue(activeMealPlanStepAtom);
  const mealPlanInstructions = useAtomValue(mealPlanInstructionsAtom);
  const mealPlanSummary = useAtomValue(mealPlanSummaryAtom);
  const detailedMealPlanEditMode = useAtomValue(detailedMealPlanEditModeAtom);
  const { saveDetailedMealPlanEdit } = useMealPlanMutations();
  const { mutateAsync: updateInstructions } = useUpdateMealPlanInstructions();
  const { mutateAsync: updateSummary } = useUpdateMealPlanSummary();

  const handleOpenInNewTab = useCallback(() => {
    window.open(
      `/call/meal-plan/${dailyRoomName}`,
      `meal-plan-${dailyRoomName}`,
    );
    onClose();
  }, [dailyRoomName, onClose]);

  const handleSaveAndClose = async () => {
    if (
      activeMealPlanStep === MealPlanView.Instructions &&
      mealPlanInstructions
    ) {
      await updateInstructions(mealPlanInstructions);
    } else if (activeMealPlanStep === MealPlanView.Summary && mealPlanSummary) {
      await updateSummary(mealPlanSummary);
    } else if (
      activeMealPlanStep === MealPlanView.Details &&
      detailedMealPlanEditMode
    ) {
      await saveDetailedMealPlanEdit();
    }
  };

  return (
    <SessionActivityDrawer
      isOpen={isOpen}
      onClose={onClose}
      isDirty={!isReadOnly}
      placement={placement}
      confirmTitle="Leave meal planning?"
      confirmBody="Your progress on the current step will be saved."
      onConfirmClose={handleSaveAndClose}
      header={(onRequestClose) => (
        <>
          <MealPlanWizardHeader
            patientName={patientName}
            onOpenInNewTab={
              context === 'in_session' ? handleOpenInNewTab : undefined
            }
            onClose={onRequestClose}
          />
          {activeMealPlanStep === MealPlanView.Details && (
            <Flex
              w="100%"
              bg="#f3f6fe"
              borderBottomWidth="1px"
              borderColor="#e9edfa"
              px={4}
              py={2}
              justifyContent="center"
              alignItems="center"
            >
              <Text fontSize="sm" color="gray.500">
                A shopping list will automatically send with your meal plan
              </Text>
            </Flex>
          )}
        </>
      )}
      footer={(onRequestClose) => (
        <MealPlanWizardFooter
          dailyRoomName={dailyRoomName}
          onMealPlanSent={onClose}
          onClose={onRequestClose}
          onReadOnlyChange={setIsReadOnly}
          usePreviousAppointment={usePreviousAppointment}
        />
      )}
    >
      <CreateMealPlan
        variant="drawer"
        context={context}
        usePreviousAppointment={usePreviousAppointment}
      />
    </SessionActivityDrawer>
  );
};

MealPlanInSessionDrawer.displayName = 'MealPlanInSessionDrawer';
